import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import type { Denormalized } from './parser';
import { createOne, deleteOne, fetchAllPages, fetchList, fetchOne, updateOne, type CreateBody, type UpdateBody } from './client';
import type { ListQuery } from './types';

export const queryKeys = {
  list: (type: string, query?: ListQuery) =>
    ['jsonapi', type, 'list', query ?? null] as const,
  one: (type: string, id: string, query?: Pick<ListQuery, 'include' | 'fields'>) =>
    ['jsonapi', type, 'one', id, query ?? null] as const,
};

export function useResourceList<T extends Denormalized = Denormalized>(
  type: string,
  query?: ListQuery,
  options?: Omit<UseQueryOptions<{ data: T[]; total: number; meta: Record<string, unknown> }>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.list(type, query),
    queryFn: () => fetchList<T>(type, query),
    ...options,
  });
}

/**
 * Come `useResourceList`, ma legge **tutte le pagine**.
 *
 * Esiste perché `page.size` oltre 100 non è un errore: il builder lo riporta a 100 e il
 * server risponde `200` con un quinto delle righe. Una lista di riferimento troncata così
 * non si nota — la tendina si apre, mostra cento voci e sembra completa — e chi cerca la
 * centounesima conclude che il record non esiste.
 *
 * Torna la stessa forma di `useResourceList` (`{ data, total, meta }`), così un call site si
 * converte cambiando il nome dell'hook e togliendo `page`, senza toccare chi legge.
 *
 * `tetto` limita il numero di righe raccolte: serve a non trasformare una lista cresciuta
 * troppo in venti richieste di fila. Va scelto pensando al caso peggiore reale.
 */
export function useResourceListAll<T extends Denormalized = Denormalized>(
  type: string,
  query?: Omit<ListQuery, 'page'>,
  options?: Omit<UseQueryOptions<{ data: T[]; total: number; meta: Record<string, unknown> }>, 'queryKey' | 'queryFn'>,
  cap = 2000,
) {
  return useQuery({
    queryKey: [...queryKeys.list(type, query), 'all', cap],
    queryFn: async () => {
      const data = await fetchAllPages<T>(type, query, cap);
      return { data, total: data.length, meta: {} as Record<string, unknown> };
    },
    ...options,
  });
}

export function useResource<T extends Denormalized = Denormalized>(
  type: string,
  id: string | undefined,
  query?: Pick<ListQuery, 'include' | 'fields'>,
  options?: Omit<UseQueryOptions<T | null>, 'queryKey' | 'queryFn' | 'enabled'>
) {
  return useQuery({
    queryKey: queryKeys.one(type, id ?? '', query),
    queryFn: () => fetchOne<T>(type, id as string, query),
    enabled: Boolean(id),
    ...options,
  });
}

export function useCreateResource<T extends Denormalized = Denormalized>(type: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBody) => createOne<T>(type, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['jsonapi', type, 'list'] });
    },
  });
}

export function useUpdateResource<T extends Denormalized = Denormalized>(type: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateBody) => updateOne<T>(type, body),
    onSuccess: (_, body) => {
      qc.invalidateQueries({ queryKey: ['jsonapi', type, 'list'] });
      qc.invalidateQueries({ queryKey: ['jsonapi', type, 'one', body.id] });
    },
  });
}

export function useDeleteResource(type: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteOne(type, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['jsonapi', type] });
    },
  });
}
